import React from "react";
import { Form, InputGroup, FormControl } from "react-bootstrap";
import ColorPicker from "../../../lib/plugins/color-picker/ColorPicker";
import {
  PAGE_NUMBER_POSITIONS,
  PAGE_NUMBER_FORMATS,
  getPageNumberSettings,
  formatPageNumber,
} from "../../../lib/global/helpers";

// Grid order for the position picker: top row, then bottom row.
const PICKER_ROWS = [
  ["topLeft", "topCenter", "topRight"],
  ["bottomLeft", "bottomCenter", "bottomRight"],
];

const labelFor = (value) => PAGE_NUMBER_POSITIONS.find((p) => p.value === value)?.label || value;

// "Page numbers" block in the Properties panel. Reads/writes
// `prop.pageNumber` through `onChange(nextPageNumber)`.
export default function PageNumberSettings({ prop, onChange }) {
  const settings = getPageNumberSettings(prop);
  const update = (patch) => onChange({ ...(prop?.pageNumber || {}), ...settings, ...patch });

  return (
    <div id="page-number-settings" className="page-number-settings">
      {/* Bootstrap 5 checkbox markup, so the label sits beside the box. */}
      <div className="form-check">
        <input
          className="form-check-input"
          type="checkbox"
          id="forShowPageNumbers"
          checked={settings.show}
          onChange={(e) => update({ show: e.target.checked })}
        />
        <label className="form-check-label" htmlFor="forShowPageNumbers">
          Show Page Numbers
        </label>
      </div>

      {settings.show && (
        <div className="d-flex align-items-start mt-2" style={{ gap: "16px" }}>
          {/* Left: format, font size and colour, one per row. */}
          <div style={{ flex: "1 1 auto", minWidth: 0 }}>
            <Form.Group className="mb-2" controlId="forPageNumberFormat">
              <Form.Label className="mb-1">Format</Form.Label>
              <select
                className="form-select form-select-sm"
                style={{ height: "32px", borderRadius: "4px" }}
                value={settings.format}
                onChange={(e) => update({ format: e.target.value })}
              >
                {PAGE_NUMBER_FORMATS.map((f) => (
                  <option key={f.value} value={f.value}>
                    {formatPageNumber(f.value, 1, 3)}
                  </option>
                ))}
              </select>
            </Form.Group>

            {/* Font size and colour on one row, together as wide as the
                format dropdown above. */}
            <div className="d-flex align-items-end flex-nowrap" style={{ gap: "8px" }}>
            <Form.Group controlId="forPageNumberFontSize" style={{ width: "80px", flex: "0 0 auto" }}>
              <Form.Label className="mb-1">Font Size</Form.Label>
              {/* Same 32px height and 4px radius as the colour picker's fields. */}
              <InputGroup size="sm" style={{ flexWrap: "nowrap" }}>
                <FormControl
                  type="number"
                  min="6"
                  max="24"
                  value={settings.fontSize}
                  style={{ height: "32px", borderRadius: "4px 0 0 4px" }}
                  onChange={(e) => {
                    const size = parseInt(e.target.value, 10);
                    if (size >= 1) update({ fontSize: size });
                  }}
                />
                <InputGroup.Text style={{ height: "32px", borderRadius: "0 4px 4px 0" }}>pt</InputGroup.Text>
              </InputGroup>
            </Form.Group>

            <Form.Group className="compact-colour-field" style={{ flex: "1 1 auto", minWidth: 0 }}>
              <Form.Label className="mb-1">Colour</Form.Label>
              <ColorPicker
                color={settings.color}
                callbackFromParents={(e) => update({ color: e.hex })}
              />
            </Form.Group>
            </div>
          </div>

          {/* Right: position picker. */}
          <div style={{ flex: "0 0 auto" }}>
            <Form.Label className="mb-1">Position</Form.Label>
            <div className="page-number-picker" role="radiogroup" aria-label="Page number position">
              {PICKER_ROWS.map((row, rowIndex) => (
                <div key={rowIndex} className={"page-number-picker-row" + (rowIndex === 0 ? " is-top" : " is-bottom")}>
                  {row.map((value) => (
                    <button
                      key={value}
                      type="button"
                      role="radio"
                      aria-checked={settings.position === value}
                      title={labelFor(value)}
                      className={"page-number-picker-spot" + (settings.position === value ? " active" : "")}
                      onClick={() => update({ position: value })}
                    />
                  ))}
                </div>
              ))}
            </div>
            <Form.Text className="text-muted d-block">{labelFor(settings.position)}</Form.Text>
          </div>
        </div>
      )}
    </div>
  );
}
